// src/pages/VendorSubscribePage.js
// Public vendor self-signup page — accessible at /vendor/subscribe
// Optional ?mall=SLUG pre-selects a mall and restricts to mall-compatible models.
// Steps: 1 Business info → 2 Choose model → 3 Mall (conditional) → Submit

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api         from '../services/api';

// ── Deployment model definitions ─────────────────────────────
const MODELS = [
  {
    id:      'STANDALONE_FULL',
    icon:    '🏪',
    label:   'Model 1a — Solo (full service)',
    tagline: 'Everything included — kiosk, orders, kitchen, payments, analytics.',
    bullets: ['Own kiosk counter', 'Full kitchen display', 'Payments & invoicing', 'Analytics dashboard'],
    mall:    false,
    color:   '#185FA5',
    bg:      '#1a2d3d',
    border:  '#2a5080',
  },
  {
    id:      'STANDALONE_POS_BRIDGE',
    icon:    '🔌',
    label:   'Model 1b — Solo (POS bridge)',
    tagline: 'Already have a POS? Use QuickServe as the kiosk front-end.',
    bullets: ['Kiosk sends orders to your POS', 'Real-time order sync', 'No double-entry'],
    mall:    false,
    color:   '#3B6D11',
    bg:      '#1a3d1a',
    border:  '#2a6020',
  },
  {
    id:      'MALL_MANAGED',
    icon:    '🏬',
    label:   'Model 2 — Mall managed',
    tagline: 'Join a food court. The mall manages kiosk and billing for you.',
    bullets: ['Appears in shared mall kiosk', 'Mall handles payments', 'Menu & orders in your panel'],
    mall:    true,
    color:   '#854F0B',
    bg:      '#3d2e10',
    border:  '#6a4010',
  },
  {
    id:      'MALL_PLUS_STALL',
    icon:    '🏬+🏪',
    label:   'Model 3a — Mall + stall kiosk',
    tagline: 'Best of both — appear in the mall kiosk AND have your own counter kiosk.',
    bullets: ['Shared mall kiosk presence', 'Own stall counter kiosk', 'Full analytics & billing'],
    mall:    true,
    color:   '#534AB7',
    bg:      '#2a1a3d',
    border:  '#4a3a9a',
  },
  {
    id:      'TABLE_QR_ONLY',
    icon:    '📱',
    label:   'Model 3b — Table QR only',
    tagline: 'Table-service restaurants — customers scan QR at the table to order.',
    bullets: ['No counter kiosk needed', 'QR codes per table', 'Kitchen display system'],
    mall:    false,
    color:   '#0F6E56',
    bg:      '#1a3d3d',
    border:  '#1a6050',
  },
];

const MALL_MODEL_IDS = ['MALL_MANAGED', 'MALL_PLUS_STALL'];

// ── Step indicator ────────────────────────────────────────────
function Steps({ current, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 36 }}>
      {Array.from({ length: total }, (_, i) => {
        const n    = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <React.Fragment key={n}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0,
              background: done ? 'var(--green)' : active ? '#f5a623' : 'var(--bg3)',
              color:      done ? '#000' : active ? '#000' : 'var(--text3)',
              border:     `2px solid ${done ? 'var(--green)' : active ? '#f5a623' : 'var(--border)'}`,
              transition: 'all .2s',
            }}>
              {done ? '✓' : n}
            </div>
            {n < total && (
              <div style={{ width: 40, height: 2, background: done ? 'var(--green)' : 'var(--border)', flexShrink: 0 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function VendorSubscribePage() {
  // Read optional ?mall=SLUG from URL
  const urlParams   = new URLSearchParams(window.location.search);
  const mallSlugParam = urlParams.get('mall') || '';

  const { registerVendor } = useAuth();

  // This page is closed unless self-signup is enabled. Until the server confirms
  // it is open (and on any error) nothing is shown and the visitor is sent home.
  const [signupOpen, setSignupOpen] = useState(false);
  useEffect(() => {
    api.get('/config/signup-status')
      .then(r => {
        if (r.data?.selfSignupEnabled === true) setSignupOpen(true);
        else window.location.href = '/';
      })
      .catch(() => { window.location.href = '/'; });
  }, []);

  // Step state
  const [step,    setStep]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Step 1 — business info
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [phone,    setPhone]    = useState('');
  const [emoji,    setEmoji]    = useState('🍽');

  // Step 2 — deployment model
  const [modelId, setModelId] = useState('STANDALONE_FULL');

  // Step 3 — mall selection
  const [malls,  setMalls]  = useState([]);
  const [mallId, setMallId] = useState('');

  const isMallModel = MALL_MODEL_IDS.includes(modelId);

  // If a mall slug is pre-supplied, fetch its id and lock the mall
  useEffect(() => {
    api.get('/public/malls')
      .then(r => {
        const list = r.data || [];
        setMalls(list);
        if (mallSlugParam) {
          const found = list.find(m => m.slug === mallSlugParam);
          if (found) {
            setMallId(found.id);
            // Pre-select MALL_MANAGED when arriving via a mall link
            setModelId('MALL_MANAGED');
          }
        }
      })
      .catch(() => {});
  }, [mallSlugParam]);

  // Visible models: if a mall was pre-set via URL, only show mall-compatible options
  const visibleModels = mallSlugParam
    ? MODELS.filter(m => m.mall)
    : MODELS;

  // Total steps: 3 if mall model chosen, 2 otherwise
  const totalSteps = isMallModel ? 3 : 2;

  // ── Validation ────────────────────────────────────────────
  const validateStep1 = () => {
    if (!name.trim())                     return 'Business name is required';
    if (!email.includes('@'))             return 'Enter a valid email';
    if (password.length < 8)             return 'Password must be at least 8 characters';
    if (password !== confirm)            return 'Passwords do not match';
    return '';
  };

  const next = () => {
    setError('');
    if (step === 1) {
      const err = validateStep1();
      if (err) { setError(err); return; }
    }
    if (step === 2 && isMallModel) { setStep(3); return; }
    if (step === 2 && !isMallModel) { handleSubmit(); return; }
    if (step === 3) { handleSubmit(); return; }
    setStep(s => s + 1);
  };

  const back = () => { setError(''); setStep(s => s - 1); };

  // ── Submit ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isMallModel && !mallId) { setError('Please select a mall'); return; }
    setLoading(true); setError('');
    try {
      await registerVendor({
        name:           name.trim(),
        email:          email.trim().toLowerCase(),
        password,
        emoji:          emoji || '🍽',
        phone:          phone || undefined,
        deploymentMode: modelId,
        mallId:         isMallModel ? mallId : undefined,
      });
      // registerVendor auto-logs in → App.js will now render VendorApp
      window.location.href = '/';
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  const selectedModel = MODELS.find(m => m.id === modelId);

  if (!signupOpen) return null; // closed (or not yet confirmed open)

  return (
    <div style={{
      minHeight: '100vh', background: '#090d1a',
      color: '#f0ede8', fontFamily: "'Segoe UI', system-ui, sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '40px 20px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>⚡ QuickServe</div>
        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>Start your free account</div>
        <div style={{ fontSize: 14, color: '#888' }}>
          Already have an account?{' '}
          <a href="/" style={{ color: '#f5a623', textDecoration: 'none', fontWeight: 600 }}>Sign in →</a>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: '#111827', border: '1px solid #1f2937',
        borderRadius: 20, padding: '36px 32px',
        width: '100%', maxWidth: step === 2 ? 780 : 480,
        transition: 'max-width .3s ease',
      }}>
        <Steps current={step} total={totalSteps} />

        {/* ── Step 1: Business info ── */}
        {step === 1 && (
          <>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, textAlign: 'center' }}>Your Business</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 24, textAlign: 'center' }}>Tell us about your stall or restaurant</div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Business name *</div>
              <input type="text" value={name} placeholder="e.g. Shawarma Corner"
                onChange={e => setName(e.target.value)}
                style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 14, width: '100%', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Business email *</div>
              <input type="email" value={email} placeholder="you@yourbusiness.com"
                onChange={e => setEmail(e.target.value)}
                style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 14, width: '100%', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Password * (min 8 chars)</div>
                <input type="password" value={password} placeholder="••••••••"
                  onChange={e => setPassword(e.target.value)}
                  style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 14, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Confirm password *</div>
                <input type="password" value={confirm} placeholder="••••••••"
                  onChange={e => setConfirm(e.target.value)}
                  style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 14, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 12, marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Phone (optional)</div>
                <input type="tel" value={phone} placeholder="+971 50 123 4567"
                  onChange={e => setPhone(e.target.value)}
                  style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 14, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Emoji icon</div>
                <input type="text" value={emoji} maxLength={4}
                  onChange={e => setEmoji(e.target.value)}
                  placeholder="🍽"
                  style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, padding: '10px 14px', color: '#f0ede8', fontSize: 22, textAlign: 'center', width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
          </>
        )}

        {/* ── Step 2: Choose deployment model ── */}
        {step === 2 && (
          <>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, textAlign: 'center' }}>Choose your plan</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 24, textAlign: 'center' }}>
              Pick the model that fits how your business operates
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 24 }}>
              {visibleModels.map(m => {
                const active = modelId === m.id;
                return (
                  <div key={m.id} onClick={() => setModelId(m.id)}
                    style={{
                      padding: '16px 14px', borderRadius: 12, cursor: 'pointer',
                      border: `2px solid ${active ? m.color : '#1f2937'}`,
                      background: active ? m.bg : '#1a1f2e',
                      transition: 'all .15s',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>{m.icon}</span>
                      <div style={{ fontSize: 12, fontWeight: 700, color: active ? m.color : '#ccc' }}>{m.label}</div>
                      {active && <div style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: m.color }} />}
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 8, lineHeight: 1.5 }}>{m.tagline}</div>
                    <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: active ? '#bbb' : '#666', lineHeight: 1.8 }}>
                      {m.bullets.map(b => <li key={b}>{b}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Step 3: Mall selection ── */}
        {step === 3 && isMallModel && (
          <>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, textAlign: 'center' }}>Select your mall</div>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 24, textAlign: 'center' }}>
              Choose the food court or mall you're operating in
            </div>

            {malls.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 0', color: '#888', fontSize: 13 }}>
                No malls available yet. Contact your mall admin to get set up.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                {malls.map(m => (
                  <div key={m.id} onClick={() => setMallId(m.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${mallId === m.id ? '#f5a623' : '#1f2937'}`,
                      background: mallId === m.id ? '#3d2e10' : '#1a1f2e',
                      transition: 'all .15s',
                    }}>
                    <span style={{ fontSize: 22 }}>🏬</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: mallId === m.id ? '#f5a623' : '#f0ede8' }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{m.slug}</div>
                    </div>
                    {mallId === m.id && <span style={{ color: '#f5a623', fontSize: 18 }}>✓</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div style={{ padding: '12px 14px', background: '#1a1f2e', borderRadius: 8, border: '1px solid #1f2937', fontSize: 12, color: '#888', marginBottom: 20 }}>
              <span style={{ color: '#f5a623', fontWeight: 600 }}>{emoji} {name}</span>
              {' · '}
              <span>{MODELS.find(m => m.id === modelId)?.label}</span>
              {mallId && <span>{' · '}{malls.find(m => m.id === mallId)?.name}</span>}
            </div>
          </>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: '#3d1a1a', color: '#e05a5a', fontSize: 13, border: '1px solid #5a2a2a' }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {step > 1 && (
            <button onClick={back} disabled={loading}
              style={{ flex: 1, padding: '12px 0', borderRadius: 10, background: 'transparent', color: '#888', border: '1px solid #374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              ← Back
            </button>
          )}
          <button onClick={next} disabled={loading || (step === 3 && isMallModel && !mallId)}
            style={{
              flex: 2, padding: '13px 0', borderRadius: 10,
              background: loading ? '#555' : '#f5a623',
              color: '#000', border: 'none', fontWeight: 700, fontSize: 15,
              cursor: loading ? 'not-allowed' : 'pointer', transition: 'opacity .15s',
            }}>
            {loading ? '⏳ Creating account...'
              : step === totalSteps ? '🚀 Create My Account'
              : 'Continue →'}
          </button>
        </div>

        {/* Legal */}
        {step === totalSteps && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: '#555' }}>
            By creating an account you agree to QuickServe's terms of service.
          </div>
        )}
      </div>

      {/* Pre-selected mall badge */}
      {mallSlugParam && (
        <div style={{ marginTop: 16, fontSize: 12, color: '#f5a623', background: '#3d2e10', padding: '6px 16px', borderRadius: 20, border: '1px solid #6a4010' }}>
          🏬 Signing up for <strong>{malls.find(m => m.slug === mallSlugParam)?.name || mallSlugParam}</strong>
        </div>
      )}
    </div>
  );
}
